import { memo } from "react";
import { styled } from "styled-components";

import { Image } from "common/components";
import { breakWordStyle, lineTruncationStyle } from "common/styles";
import { useFormatDate } from "common/utilities";
import { ExpandableCaseStudyItemWrapper } from "domains/caseStudy/components/ExpandableCaseStudyItemWrapper";
import type { CaseStudy } from "domains/caseStudy/types";
import { KnowledgeDetailButton } from "domains/publication/components/KnowledgeDetailButton";

const StyledTitle = styled.div`
  font-size: 1rem;
  line-height: 1.4rem;

  ${breakWordStyle}
  ${lineTruncationStyle(2)}
`;

const StyledRow = styled.div`
  display: flex;
  margin-right: 1rem;
`;

const StyledOuterRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledImage = styled(Image)`
  width: 4rem;
  min-width: 4rem;
  height: 4rem;
  border-radius: 5px;
  margin-right: 1rem;
  object-fit: contain;
`;

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  align-self: center;
`;

const StyledDateLabel = styled.div`
  color: ${({ theme }) => theme.colors.textFade};
  font-weight: bold;
`;

const StyledSourceContainer = styled.div`
  display: flex;
  width: 100%;
  font-size: 12px;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
  padding-top: 0.5rem;
`;

const StyledText = styled.p`
  margin: 0;
  font-weight: bold;
  ${lineTruncationStyle(1)};
`;

const StyledKnowledgeDetailButton = styled(KnowledgeDetailButton)`
  flex: 0;
`;

type ExpandableCaseStudyItemProps = { caseStudy: CaseStudy };

export const ExpandableCaseStudyItem = memo(({ caseStudy }: ExpandableCaseStudyItemProps) => {
  const { getRelativeDate } = useFormatDate();
  const { title, link, time, images, authors } = caseStudy;
  const authorName = authors[0]?.name;

  const showSourceContainer = Boolean(authorName || time);

  return (
    <ExpandableCaseStudyItemWrapper caseStudy={caseStudy}>
      <StyledOuterRow title={title}>
        <StyledRow>
          <StyledImage src={images.thumbnail} alt={title} />
          <StyledContent>
            <StyledTitle>{title}</StyledTitle>
            {showSourceContainer && (
              <StyledSourceContainer>
                {authorName && <StyledText>{authorName}</StyledText>}
                {time !== 0 && (
                  <StyledDateLabel>
                    {getRelativeDate({
                      date: time,
                      precision: "hour",
                      onlyDate: true,
                    })}
                  </StyledDateLabel>
                )}
              </StyledSourceContainer>
            )}
          </StyledContent>
        </StyledRow>
        <StyledKnowledgeDetailButton link={link} title={caseStudy.title} />
      </StyledOuterRow>
    </ExpandableCaseStudyItemWrapper>
  );
});

ExpandableCaseStudyItem.displayName = "ExpandableCaseStudyItem";
